// Hand a dropped contract from Vio to the scanner.
//
// Vio floats on every page; the scanner lives on /scan. Something has to
// carry the text across that navigation, and none of the obvious options work:
// a File cannot go in sessionStorage, a query string cannot hold 30k
// characters, and a full page load would drop it anyway.
//
// A module singleton can, because Next's App Router navigates on the client
// and keeps the JS context alive across a `router.push`. The value survives
// exactly as long as it needs to and no longer — `take()` clears it, so a
// later visit to /scan never resurrects a contract someone dropped once.
//
// Deliberately NOT persisted anywhere. The public scanner is unauthenticated
// and the text is somebody's contract; parking it in sessionStorage would
// leave it readable by every script on the origin until the tab closed.

let pending: string | null = null;

export function put(text: string): void {
  pending = text;
}

/** Read and clear. Returns null when nothing is waiting. */
export function take(): string | null {
  const t = pending;
  pending = null;
  return t;
}

/** What the scanner will accept — mirrored from src/app/scan/page.tsx. */
export const MIN_CHARS = 100;
export const MAX_CHARS = 30000;

/** Extensions we can turn into text in the browser, with no parser. */
export const TEXT_EXTENSIONS = [".txt", ".md", ".markdown", ".rtf", ".csv"] as const;

export function isPlainTextFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    file.type.startsWith("text/") ||
    TEXT_EXTENSIONS.some((ext) => name.endsWith(ext))
  );
}

// ── Is a scan actually running? ────────────────────────────────────────────
//
// Vio has a `scanning` state — flattened eyes, a beam sweeping the visor, a
// pulsing aura. It would have been much easier to play that for a second
// after a drop and then navigate, and it would have been a lie: during that
// second nothing is being scanned, the text is being carried to /scan.
//
// This site carries a claims guard (src/lib/copy/noUnbackedClaims.test.ts)
// because its copy has outrun the product twice already, and an animation
// asserting work that is not happening is that same class of thing wearing a
// different hat — just harder to grep for. So `scanning` is wired to the one
// moment a scan is genuinely in flight: /scan sets this around its POST to
// /api/public-scan, and VioFab subscribes. Everywhere else Vio can be curious,
// or pleased, or sorry — but it cannot claim to be reading.
//
// A module singleton for the same reason `pending` is one: the App Router
// keeps the JS context alive across a client navigation, so the scanner and
// the character that floats above it share this without a store or a context.

type BusyListener = (busy: boolean) => void;

let busy = false;
const listeners = new Set<BusyListener>();

/** Called by the scanner around a real request. */
export function setScanning(next: boolean): void {
  if (busy === next) return;
  busy = next;
  for (const fn of listeners) fn(next);
}

export function isScanning(): boolean {
  return busy;
}

/** Subscribe; returns the unsubscribe function. */
export function onScanning(fn: BusyListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
